/**
 * EditKeyModal — Edit an existing API key's name, scopes, rate limit, webhook.
 *
 * Props:
 *   open      : boolean
 *   keyData   : object | null  — the key record from listKeys()
 *   onCancel  : () => void
 *   onSuccess : () => void     — called after successful update
 *   wabaReady : boolean
 */
import React, { useState, useEffect } from 'react';
import {
  Modal, Input, Button, Checkbox, message, Typography, InputNumber,
  Switch, Tooltip, Spin, Divider, Tag, DatePicker,
} from 'antd';
import {
  EditOutlined, WarningOutlined, GlobalOutlined, InfoCircleOutlined,
  SafetyOutlined, PlusOutlined, IdcardOutlined,
} from '@ant-design/icons';
import ApiKeyService from '../../../../../services/apiKeyService';
import moment from 'moment';

const { Text } = Typography;

// Fallback scope groups
const FALLBACK_SCOPE_GROUPS = [
  {
    label: 'Contacts & Addressbooks',
    scopes: [
      { value: 'contacts:read', desc: 'Read contacts and addressbook data' },
      { value: 'contacts:write', desc: 'Create, update, delete contacts and addressbooks' },
    ],
  },
  {
    label: 'WhatsApp Templates',
    requiresWaba: true,
    scopes: [
      { value: 'templates:read', desc: 'Read WhatsApp templates' },
      { value: 'templates:write', desc: 'Create, update, delete WhatsApp templates' },
    ],
  },
  {
    label: 'Email Templates',
    scopes: [
      { value: 'email_templates:read', desc: 'Read email templates' },
      { value: 'email_templates:write', desc: 'Create, update, delete email templates' },
    ],
  },
  {
    label: 'WhatsApp Campaigns',
    requiresWaba: true,
    scopes: [
      { value: 'campaigns:read', desc: 'Read WhatsApp campaign data and analytics' },
      { value: 'campaigns:write', desc: 'Create and update WhatsApp campaigns' },
      { value: 'campaigns:execute', desc: 'Send/start WhatsApp campaigns' },
    ],
  },
  {
    label: 'Email Campaigns',
    scopes: [
      { value: 'email_campaigns:read', desc: 'Read email campaign data and analytics' },
      { value: 'email_campaigns:write', desc: 'Create and update email campaigns' },
      { value: 'email_campaigns:execute', desc: 'Send/start email campaigns' },
    ],
  },
  {
    label: 'Transactional Messages',
    requiresWaba: true,
    scopes: [
      { value: 'messages:send', desc: 'Send transactional WhatsApp messages via API' },
      { value: 'messages:read', desc: 'Read message status and history' },
    ],
  },
  {
    label: 'Webhooks',
    scopes: [
      { value: 'webhooks:manage', desc: 'Create, update and delete webhook subscriptions' },
    ],
  },
];

const WEBHOOK_EVENTS = [
  { value: 'message.delivered', label: 'Message Delivered' },
  { value: 'message.read', label: 'Message Read' },
  { value: 'message.failed', label: 'Message Failed' },
  { value: 'message.sent', label: 'Message Sent' },
  { value: 'message.received', label: 'Message Received' },
  { value: 'campaign.completed', label: 'Campaign Completed' },
  { value: 'template.approved', label: 'Template Approved' },
  { value: 'template.rejected', label: 'Template Rejected' },
  { value: 'contact.opted_out', label: 'Contact Opted Out' },
];

function buildScopeGroups(apiScopes, descriptions, apiGroups) {
  if (!apiScopes || !Array.isArray(apiScopes) || !apiScopes.length) return FALLBACK_SCOPE_GROUPS;
  // Use server-provided groups array when available
  if (Array.isArray(apiGroups) && apiGroups.length > 0) {
    const wabaLabels = ['WhatsApp Templates', 'WhatsApp Campaigns', 'Transactional Messages'];
    return apiGroups
      .filter((g) => Array.isArray(g.scopes) && g.scopes.length > 0)
      .map((g) => ({
        label: g.label,
        requiresWaba: wabaLabels.includes(g.label),
        scopes: g.scopes.map((s) => ({ value: s, desc: descriptions?.[s] || s })),
      }));
  }
  // Fallback: group by prefix
  const groupMap = {};
  const labels = {
    contacts: 'Contacts & Addressbooks', templates: 'WhatsApp Templates',
    email_templates: 'Email Templates', campaigns: 'WhatsApp Campaigns',
    email_campaigns: 'Email Campaigns', messages: 'Transactional Messages',
    webhooks: 'Webhooks',
  };
  const wabaGroups = ['templates', 'campaigns', 'messages'];
  apiScopes.forEach((s) => {
    const prefix = s.includes(':') ? s.split(':')[0] : s;
    if (!groupMap[prefix]) {
      groupMap[prefix] = { label: labels[prefix] || prefix, requiresWaba: wabaGroups.includes(prefix), scopes: [] };
    }
    groupMap[prefix].scopes.push({ value: s, desc: descriptions?.[s] || s });
  });
  return Object.values(groupMap);
}

export default function EditKeyModal({ open, keyData, onCancel, onSuccess, wabaReady = true }) {
  const [keyName, setKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [rateLimit, setRateLimit] = useState(60);
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [webhookSecret, setWebhookSecret] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scopeGroups, setScopeGroups] = useState(FALLBACK_SCOPE_GROUPS);
  const [scopesLoading, setScopesLoading] = useState(false);
  // IP Whitelist
  const [allowedIps, setAllowedIps] = useState([]);
  const [ipInput, setIpInput] = useState('');


  // Populate form from keyData whenever it changes
  useEffect(() => {
    if (keyData && open) {
      setKeyName(keyData.key_name || keyData.keyName || '');
      setSelectedScopes(Array.isArray(keyData.scopes) ? [...keyData.scopes] : []);
      setRateLimit(keyData.rate_limit_per_minute || keyData.rateLimitPerMinute || 60);
      const hasWebhook = !!(keyData.webhook_url || keyData.webhookUrl);
      setWebhookEnabled(hasWebhook);
      setWebhookUrl(keyData.webhook_url || keyData.webhookUrl || '');
      setWebhookEvents(Array.isArray(keyData.webhook_events || keyData.webhookEvents)
        ? [...(keyData.webhook_events || keyData.webhookEvents)]
        : []);
      setWebhookSecret(keyData.webhook_secret || keyData.webhookSecret || '');
      setExpiresAt(keyData.expires_at || keyData.expiresAt
        ? (typeof (keyData.expires_at || keyData.expiresAt) === 'string'
          ? moment(keyData.expires_at || keyData.expiresAt)
          : keyData.expires_at || keyData.expiresAt)
        : null);
      setAllowedIps(Array.isArray(keyData.allowed_ips || keyData.allowedIps)
        ? [...(keyData.allowed_ips || keyData.allowedIps)]
        : []);
      setIpInput('');
    }
  }, [keyData, open]);

  // Fetch scopes once when modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setScopesLoading(true);
      try {
        const res = await ApiKeyService.getScopes();
        if (!cancelled && res.success && res.entity) {
          const g = buildScopeGroups(
            res.entity.scopes,
            res.entity.description,
            res.entity.groups,
          );
          if (g.length) setScopeGroups(g);
        }
      } catch { /* use fallback */ }
      finally { if (!cancelled) setScopesLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handleScopeToggle = (scope, checked) => {
    setSelectedScopes((p) => checked ? [...p, scope] : p.filter((s) => s !== scope));
  };

  const handleGroupSelectAll = (group, checked) => {
    const vals = group.scopes.map((s) => s.value);
    setSelectedScopes((p) => { const w = p.filter((s) => !vals.includes(s)); return checked ? [...w, ...vals] : w; });
  };

  const isGroupAllSelected = (g) => g.scopes.every((s) => selectedScopes.includes(s.value));
  const isGroupPartial = (g) => g.scopes.some((s) => selectedScopes.includes(s.value)) && !isGroupAllSelected(g);

  const handleSave = async () => {
    if (!keyData) return;
    if (!keyName.trim()) { message.warning('Key name is required'); return; }
    if (selectedScopes.length === 0) { message.warning('Select at least one permission'); return; }

    const payload = {};
    // Only send changed fields
    const origName = keyData.key_name || keyData.keyName || '';
    if (keyName.trim() !== origName) payload.keyName = keyName.trim();
    const origScopes = Array.isArray(keyData.scopes) ? keyData.scopes : [];
    if (JSON.stringify([...selectedScopes].sort()) !== JSON.stringify([...origScopes].sort())) {
      payload.scopes = selectedScopes;
    }
    const origRate = keyData.rate_limit_per_minute || keyData.rateLimitPerMinute || 60;
    if (rateLimit !== origRate) payload.rateLimitPerMinute = rateLimit;

    // Allowed IPs
    const origIps = Array.isArray(keyData.allowed_ips || keyData.allowedIps) ? (keyData.allowed_ips || keyData.allowedIps) : [];
    if (JSON.stringify([...allowedIps].sort()) !== JSON.stringify([...origIps].sort())) {
      payload.allowedIps = allowedIps;
    }

    // ExpiresAt
    const origExpires = keyData.expires_at || keyData.expiresAt || null;
    const newExpires = expiresAt ? expiresAt.toISOString() : null;
    if (newExpires !== origExpires) {
      payload.expiresAt = newExpires;
    }

    // Webhook
    if (webhookEnabled && webhookUrl.trim()) {
      payload.webhookUrl = webhookUrl.trim();
      payload.webhookEvents = webhookEvents;
      if (webhookSecret.trim()) payload.webhookSecret = webhookSecret.trim();
    } else if (!webhookEnabled && (keyData.webhook_url || keyData.webhookUrl)) {
      // Disable webhook by sending empty
      payload.webhookUrl = '';
      payload.webhookEvents = [];
    }

    if (Object.keys(payload).length === 0) {
      message.info('No changes detected');
      onCancel();
      return;
    }

    setLoading(true);
    try {
      const id = keyData._id || keyData.id;
      const result = await ApiKeyService.updateKey(id, payload);
      if (!result.success) {
        message.error(typeof result.error === 'string' ? result.error : 'Failed to update key');
        setLoading(false);
        return;
      }
      message.success('API key updated');
      onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error;
      message.error(typeof msg === 'string' ? msg : 'Failed to update API key');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={<span className="api-key-modal-title"><EditOutlined /> Edit: {keyData?.key_name || keyData?.keyName || 'API Key'}</span>}
      footer={null}
      width={580}
      destroyOnClose
      className="generate-key-modal"
    >
      {/* Key ID (read-only) */}
      {(keyData?.key_id || keyData?.keyId) && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f6f8fa', borderRadius: 8, border: '1px solid #e8e8e8' }}>
          <IdcardOutlined style={{ color: '#1890ff', marginRight: 8 }} />
          <Text strong style={{ fontSize: 13 }}>Key ID: </Text>
          <Text code style={{ fontSize: 12, fontFamily: 'monospace' }}>{keyData?.key_id || keyData?.keyId}</Text>
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>(cannot change)</Text>
        </div>
      )}

      {/* Key Name */}
      <div className="api-key-form-section">
        <Text strong className="api-key-label">Key Name *</Text>
        <Input value={keyName} onChange={(e) => setKeyName(e.target.value)} maxLength={60} size="large" />
      </div>

      {/* Rate Limit + Expiry row */}
      <div className="api-key-form-grid api-key-form-section">
        <div className="api-key-form-col">
          <Text strong className="api-key-label">
            Rate Limit (req/min)
            <Tooltip title="Max requests per minute for this key"><InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} /></Tooltip>
          </Text>
          <InputNumber min={10} max={1000} value={rateLimit} onChange={setRateLimit} style={{ width: '100%' }} />
        </div>
        <div className="api-key-form-col">
          <Text strong className="api-key-label">Expiry Date (optional)</Text>
          <DatePicker
            style={{ width: '100%' }}
            value={expiresAt}
            onChange={setExpiresAt}
            disabledDate={(d) => d && d.isBefore(new Date(), 'day')}
            placeholder="Never expires"
            allowClear
          />
        </div>
      </div>

      {/* Permissions */}
      <Text strong className="api-key-label api-key-label--section">Permissions *</Text>
      {scopesLoading ? (
        <div className="api-key-loading"><Spin size="small" /> Loading scopes...</div>
      ) : (
        scopeGroups.map((group) => (
          <div key={group.label} className="scope-group">
            <div className="scope-group-header">
              <h4>
                {group.label}
                {group.requiresWaba && !wabaReady && (
                  <Tooltip title="WhatsApp scopes require embedded signup.">
                    <WarningOutlined style={{ color: '#faad14', marginLeft: 6, fontSize: 13 }} />
                  </Tooltip>
                )}
              </h4>
              <Checkbox
                checked={isGroupAllSelected(group)}
                indeterminate={isGroupPartial(group)}
                onChange={(e) => handleGroupSelectAll(group, e.target.checked)}
                className="scope-group-select-all"
              >
                <Text type="secondary" style={{ fontSize: 12 }}>All</Text>
              </Checkbox>
            </div>
            {group.scopes.map((scope) => (
              <Checkbox
                key={scope.value}
                checked={selectedScopes.includes(scope.value)}
                onChange={(e) => handleScopeToggle(scope.value, e.target.checked)}
                className="scope-item-checkbox"
              >
                <span className="scope-item-desc">{scope.desc || scope.value}</span>
                <Text type="secondary" className="scope-item-code">({scope.value})</Text>
              </Checkbox>
            ))}
          </div>
        ))
      )}

      {/* Webhook Config */}
      <Divider style={{ margin: '16px 0 8px' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Switch size="small" checked={webhookEnabled} onChange={setWebhookEnabled} />
        <Text strong><GlobalOutlined /> Webhook Configuration</Text>
      </div>
      {webhookEnabled && (
        <div style={{ paddingLeft: 4 }}>
          <div style={{ marginBottom: 10 }}>
            <Text style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Webhook URL</Text>
            <Input placeholder="https://yourserver.com/webhook" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <Text style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Webhook Secret (optional)</Text>
            <Input placeholder="HMAC secret for signature verification" value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} />
          </div>
          <div>
            <Text style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Events</Text>
            {WEBHOOK_EVENTS.map((evt) => (
              <Checkbox
                key={evt.value}
                checked={webhookEvents.includes(evt.value)}
                onChange={(e) => {
                  setWebhookEvents((prev) =>
                    e.target.checked ? [...prev, evt.value] : prev.filter((v) => v !== evt.value)
                  );
                }}
                style={{ display: 'block', marginLeft: 0, marginBottom: 4 }}
              >
                {evt.label} <Text type="secondary" style={{ fontSize: 11 }}>({evt.value})</Text>
              </Checkbox>
            ))}
            <Text type="secondary" style={{ display: 'block', fontSize: 11, marginTop: 4, fontStyle: 'italic' }}>
              ℹ️ None selected = receive ALL events
            </Text>
          </div>
        </div>
      )}

      {/* IP Whitelist */}
      <Divider style={{ margin: '16px 0 8px' }} />
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <SafetyOutlined style={{ color: '#52c41a' }} /> IP Whitelist
        </Text>
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
          Restrict API key usage to specific IP addresses. Leave empty to allow all IPs.
        </Text>

        {/* Current IPs as tags */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {allowedIps.map((ip) => (
            <Tag
              key={ip}
              closable
              onClose={() => setAllowedIps((prev) => prev.filter((i) => i !== ip))}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            >
              {ip}
            </Tag>
          ))}
          {allowedIps.length === 0 && (
            <Text type="secondary" italic style={{ fontSize: 12 }}>No IP restrictions — all IPs allowed</Text>
          )}
        </div>

        {/* Add IP input */}
        <div style={{ display: 'flex', gap: 6 }}>
          <Input
            placeholder="e.g. 203.0.113.5 or 10.0.0.0/24"
            value={ipInput}
            onChange={(e) => setIpInput(e.target.value)}
            onPressEnter={() => {
              const ip = ipInput.trim();
              if (!ip) return;
              // Basic IP/CIDR validation
              const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
              if (!ipRegex.test(ip)) { message.warning('Enter a valid IPv4 address or CIDR block'); return; }
              if (allowedIps.includes(ip)) { message.info('IP already added'); return; }
              setAllowedIps((prev) => [...prev, ip]);
              setIpInput('');
            }}
            size="small"
            style={{ maxWidth: 280 }}
          />
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              const ip = ipInput.trim();
              if (!ip) return;
              const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
              if (!ipRegex.test(ip)) { message.warning('Enter a valid IPv4 address or CIDR block'); return; }
              if (allowedIps.includes(ip)) { message.info('IP already added'); return; }
              setAllowedIps((prev) => [...prev, ip]);
              setIpInput('');
            }}
          >
            Add
          </Button>
        </div>
      </div>

      {/* Actions */}
      <div style={{ marginTop: 20, textAlign: 'right', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" onClick={handleSave} loading={loading} disabled={!keyName.trim() || selectedScopes.length === 0}>
          Save Changes
        </Button>
      </div>
    </Modal>
  );
}
